import React, { type JSX } from 'react';

import Typography from '@mui/material/Typography/Typography';
import QButton from 'components/QButton';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { scheduledTransactionsSelectors } from 'app-components/flux/scheduled-transactions';
import { transactionsUtils } from 'app-components/flux/transactions';
import { isOverflowSplitItem } from 'app-components/flux/transactions/splitsHelpers';
import { splitItemsToArrayOverflowLast } from 'app-components/flux/transactions/splitsHelpers';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';
import SplitsView from 'components/TransactionSplits/SplitsView';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

const useStyles = makeStyles()((theme) => ({
  splitLine: {
    color: theme.palette.text.secondary,
    paddingTop: 6,
    display: 'flex',
    alignItems: 'center',
  },
  splitButton: {
    marginRight: 8,
  },
  splitIcon: {
    marginRight: 8,
    letterSpacing: 1,
  },
}));

type Props = {
  transaction: TransactionProps;
  onAssignSplits?: () => void;
  buttonLabel?: string;
  buttonSupportingText?: string;
  goToOtherSide?: (txn: TransactionProps) => void;
  disabledSplitsEdit?: boolean;
  viewWhenDisabled?: boolean;
  includeOverflowSplitLine?: boolean;
  /**
   * Use `series` when assigning splits to a recurring series.
   * Default is `default` for normal transaction splits.
   */
  variant?: 'default' | 'series';
};

const TransactionSplits = ({
  transaction,
  onAssignSplits,
  buttonLabel,
  buttonSupportingText,
  goToOtherSide,
  disabledSplitsEdit = false,
  viewWhenDisabled = false,
  includeOverflowSplitLine = false,
  variant = 'default',
}: Props): JSX.Element => {
  const { classes } = useStyles();

  const splitsFeatureFlag = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  const isSeriesVariant = variant === 'series';
  const splitsEditDisabled = disabledSplitsEdit ?? !splitsFeatureFlag;

  const txnAmount = transaction.amount;
  const scheduledModel = useAppSelector((state) =>
    transaction.stModelId
      ? scheduledTransactionsSelectors.getScheduledTransactionById(state, transaction.stModelId)
      : undefined,
  );

  if (isSeriesVariant && !splitsFeatureFlag) return <React.Fragment />;

  const seriesTxn = scheduledModel?.transaction ?? null;
  const splitItems = splitItemsToArrayOverflowLast(transaction.split?.items || []);

  const filteredSplitItems = splitItems.filter((item) => includeOverflowSplitLine || !isOverflowSplitItem(item));
  const headerLabel = `${filteredSplitItems.length} Splits assigned`;
  const hasSplitsAssigned = filteredSplitItems.length > 0;

  const resolvedButtonLabel = buttonLabel ?? (isSeriesVariant ? 'Assign Splits' : 'Split');
  const resolvedButtonSupportingText =
    buttonSupportingText ??
    (isSeriesVariant
      ? "When transactions are matched to this Series, they'll be split in the same way."
      : 'Assign multiple tags or categories to this transaction.');

  return (
    <>
      {hasSplitsAssigned ? (
        <SplitsView
          txnId={transaction.id}
          splitItems={filteredSplitItems}
          amount={txnAmount}
          headerLabel={headerLabel}
          showRepeatIcon={Boolean(seriesTxn && transactionsUtils.isSplitTxn(seriesTxn))}
          onEdit={splitsEditDisabled && !viewWhenDisabled ? undefined : onAssignSplits}
          disabledEdit={splitsEditDisabled}
          goToOtherSide={goToOtherSide}
          variant={variant}
        />
      ) : (
        <Typography variant='captionRegular' className={classes.splitLine}>
          <QButton
            id='assign-splits-button'
            onClick={onAssignSplits}
            disableOutline
            className={classes.splitButton}
            disabled={splitsEditDisabled}
            startIcon={<CallSplitIcon className={classes.splitIcon} />}
          >
            {resolvedButtonLabel}
          </QButton>
          {resolvedButtonSupportingText}
        </Typography>
      )}
    </>
  );
};

export default TransactionSplits;
